import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * Iter25: JWT moved from localStorage to an httpOnly `kobolog_token` cookie.
 * We no longer write the token to localStorage. On mount we probe /api/me — if
 * the cookie is valid (or the legacy Bearer header still authenticates), we
 * hydrate the user; otherwise the caller stays logged out.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try to hydrate from cookie / legacy header. Any failure = logged out.
    api.get("/me")
      .then((r) => setUser(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // `token` param is now IGNORED — the backend has already set the httpOnly
  // cookie by the time this runs. We also clear any legacy localStorage token
  // so the new cookie becomes the single source of truth.
  const login = (_token, userObj) => {
    try {
      localStorage.removeItem("kudi_token");
      // Non-sensitive UX hint so pages like /payment/success can decide
      // whether to bounce to /app/dashboard vs the landing page without
      // needing a network round-trip. The real auth lives in the cookie.
      localStorage.setItem("kobolog_logged_in", "1");
    } catch (_) { /* localStorage disabled */ }
    setUser(userObj);
  };

  const logout = () => {
    // Best-effort: ask the backend to clear the cookie. Ignore failures — the
    // client-side state is already dropped.
    api.post("/auth/logout").catch(() => {});
    try {
      localStorage.removeItem("kudi_token");
      localStorage.removeItem("kobolog_logged_in");
    } catch (_) { /* localStorage disabled */ }
    setUser(null);
  };

  const refresh = async () => {
    const r = await api.get("/me");
    setUser(r.data);
    return r.data;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
